/**
 * Quick Order Form JavaScript
 * Handles add to cart functionality and variant selection
 */

// Shared utilities
const CartUtils = {
  /**
   * Get fetch configuration for cart requests
   */
  getFetchConfig() {
    return {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    };
  },

  /**
   * Fetch current cart count from Shopify
   * @returns {Promise<number>} Cart item count
   */
  async getCartCount() {
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const cart = await response.json();
      return cart.item_count || 0;
    } catch (error) {
      console.error('Failed to fetch cart count:', error);
      return 0;
    }
  },

  /**
   * Update cart count badge in the UI
   * @param {number} count - Cart item count
   */
  updateCartCountBadge(count) {
    const badges = document.querySelectorAll('[data-cart-count]');
    badges.forEach((badge) => {
      badge.textContent = count;
      badge.setAttribute('data-cart-count', count);
      if (count > 0) {
        badge.style.display = 'inline-block';
        badge.removeAttribute('hidden');
      } else {
        badge.style.display = 'none';
        badge.setAttribute('hidden', '');
      }
    });
  },

  /**
   * Refresh cart count from server
   */
  async refreshCartCount() {
    const count = await this.getCartCount();
    this.updateCartCountBadge(count);
  },

  /**
   * Manage button loading state
   */
  setButtonLoading(button, isLoading) {
    button.disabled = isLoading;
    if (isLoading) {
      button.classList.add('loading');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      button.classList.remove('loading');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    }
  },

  /**
   * Add item to cart
   * @param {FormData|Object} data - FormData or object with variantId and quantity
   * @param {Object} options - Options for success/error callbacks
   * @returns {Promise<Object>} Response data
   */
  async addToCart(data, options = {}) {
    const { onSuccess, onError } = options;
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    
    formData.append('id', data.variantId);
    formData.append('quantity', data.quantity);

  

    config.body = formData;
    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        console.log(response);
        if (response.status) {
          onError && onError(response.description || 'Failed to add product to cart');
        } else {
          showAddedToCartModal();
          CartUtils.refreshCartCount();
          onSuccess && onSuccess('Added to cart');
        }
      })
      .catch((error) => {
        console.error(error);
        onError && onError('Failed to add product to cart');
      });
  },

  /**
   * Add multiple items to cart at once
   * @param {Array<{variantId: string|number, quantity: number}>} items
   */
  async addItemsToCart(items, options = {}) {
    const { onSuccess, onError } = options;
    if (!items.length) return;

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.variantId);
      formData.append(`items[${index}][quantity]`, item.quantity);
    });

    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          onError && onError(response.description || 'Failed to add products to cart');
        } else {
          showAddedToCartModal();
          CartUtils.refreshCartCount();
          onSuccess && onSuccess('Added to cart');
        }
      })
      .catch((error) => {
        console.error(error);
        onError && onError('Failed to add products to cart');
      });
  },

  /**
   * Format price using Shopify format or fallback
   */
  formatPrice(priceInCents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(priceInCents);
    }
    const price = priceInCents / 100;
    const currencyCode = window.Shopify?.currency?.active || '$';
    return '$'+`${price.toFixed(2)}`;
  },

  /**
   * Get unit price (in cents) for a row, preferring Bold-adjusted .money text.
   */
  getUnitPriceCentsFromRow(row) {
    // 1. Try Bold-adjusted .money element (data-product-id or data-variant-id)
    const boldMoney = row.querySelector('.money[data-product-id], .money[data-variant-id]');
    if (boldMoney) {
      const text = boldMoney.textContent.trim();
      // Strip currency symbols and spaces, keep digits, dot, comma
      const numeric = text.replace(/[^0-9.,]/g, '').replace(',', '.');
      const amount = parseFloat(numeric);
      if (!isNaN(amount)) {
        return Math.round(amount * 100); // convert to cents
      }
    }

    // 2. Fallback to original Shopify price from data-unit-price
    const unitPriceAttr =
      row.dataset.unitPrice ||
      row.querySelector('[data-unit-price]')?.dataset.unitPrice;
    return parseInt(unitPriceAttr || '0', 10) || 0;
  }
};

// Helper function to show added to cart success modal
const showAddedToCartModal = () => {
  const modal = document.getElementById('quick-order-cart-success-modal');
  if (modal) {
    modal.show();
  }
};

// Quick Order Form Component
if (!customElements.get('quick-order-form')) {
  customElements.define(
    'quick-order-form',
    class QuickOrderForm extends HTMLElement {
      constructor() {
        super();
        this.productList = this.querySelector('[data-product-list]');
        this.init();
      }

      init() {
        // Handle quantity buttons (theme updates the value; we just recalc prices) and "Add all" button
        this.addEventListener('click', (e) => {
          const quantityBtn = e.target.closest('.quantity__button');
          if (quantityBtn) {
            const quantityInput = quantityBtn
              .closest('quantity-input')
              ?.querySelector('.quantity__input');
            if (quantityInput) {
              // Let the theme's quantity script update the value first
              setTimeout(() => this.updateRowAndTotalForInput(quantityInput), 0);
            }
          }

          const addAllBtn = e.target.closest('[data-add-all]');
          if (addAllBtn) {
            e.preventDefault();
            this.handleAddAll(addAllBtn);
          }

          const viewCartBtn = e.target.closest('[data-view-cart]');
          if (viewCartBtn) {
            e.preventDefault();
            window.location.href = routes.cart_url || '/cart';
          }
        });

        // Manual typing in quantity inputs
        this.addEventListener('input', (e) => {
          if (e.target.matches('.quantity__input')) {
            this.updateRowAndTotalForInput(e.target);
          }
        });
        
        // Initialize pagination
        this.initPagination();
        
        // Initialize cart count on load
        CartUtils.refreshCartCount();
      }

      async renderSection(url) {
        if (!this.productList) return;
        
        // Show loading state
        this.productList.style.opacity = '0.5';
        this.productList.style.pointerEvents = 'none';
        
        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          const sectionElement = doc.getElementById(this.id);
          if (!sectionElement) {
            console.error('Section not found in response');
            return;
          }
          
          const newProductList = sectionElement.querySelector('[data-product-list]');
          if (newProductList) {
            this.productList.innerHTML = newProductList.innerHTML;
            this.initPagination();
            this.productList.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        } catch (error) {
          console.error('Failed to fetch section:', error);
          alert('Failed to load products. Please try again.');
        } finally {
          this.productList.style.opacity = '';
          this.productList.style.pointerEvents = '';
        }
      }

      initPagination() {
        const paginationLinks = this.querySelectorAll('[data-pagination] a');
        paginationLinks.forEach((link) => {
          link.addEventListener('click', async (e) => {
            e.preventDefault();
            const href = link.getAttribute('href');
            if (href) {
              const url = new URL(href, window.location.origin);
              const sectionId = this.dataset.sectionId;
              if (sectionId) {
                url.searchParams.set('section_id', sectionId);
              }
              
              await this.renderSection(url.toString());
              window.history.pushState({}, '', url);
            }
          });
        });
      }

      updateRowAndTotalForInput(input) {
        const row = input.closest('.quick-order-form-row');
        if (!row) return;

        const quantity = parseInt(input.value || '0', 10) || 0;
        const unitPrice = CartUtils.getUnitPriceCentsFromRow(row);

        const linePriceElement = row.querySelector('[data-line-price]');
        if (linePriceElement) {
          const lineTotal = unitPrice * quantity;
          linePriceElement.textContent = CartUtils.formatPrice(lineTotal);
        }

        this.updateTotal();
      }

      updateTotal() {
        let totalCents = 0;
        let totalQty = 0;
        const rows = this.querySelectorAll('.quick-order-form-row');
        rows.forEach((row) => {
          const unitPrice = CartUtils.getUnitPriceCentsFromRow(row);
          const quantityInput = row.querySelector('.quantity__input');
          const quantity = parseInt(quantityInput?.value || '0', 10) || 0;

          if (quantity > 0) {
            totalCents += unitPrice * quantity;
            totalQty += quantity;
          }
        });

        const totalElement = this.querySelector('[data-quick-order-total]');
        if (totalElement) {
          totalElement.textContent = CartUtils.formatPrice(totalCents);
        }

        const totalQtyElement = this.querySelector('[data-quick-order-total-qty]');
        if (totalQtyElement) {
          totalQtyElement.textContent = totalQty;
        }
      }

      async handleAddAll(button) {
        const rows = this.querySelectorAll('.quick-order-form-row');
        const items = [];

        rows.forEach((row) => {
          const variantId =
            row.dataset.variantId ||
            row.querySelector('[data-variant-id]')?.dataset.variantId ||
            row.querySelector('.quick-order-form-row__actions')?.dataset.variantId;

          const quantityInput = row.querySelector('.quantity__input');
          const quantity = parseInt(quantityInput?.value || '0', 10) || 0;

          if (variantId && quantity > 0) {
            items.push({ variantId, quantity });
          }
        });

        if (!items.length) {
          alert('Please select at least one product quantity.');
          return;
        }

        CartUtils.setButtonLoading(button, true);

        try {
          await CartUtils.addItemsToCart(items, {
            onSuccess: () => {
              console.log('Added all selected items to cart');
              // reset quantities and prices
              rows.forEach((row) => {
                const quantityInput = row.querySelector('.quantity__input');
                if (quantityInput) {
                  quantityInput.value = 0;
                  this.updateRowAndTotalForInput(quantityInput);
                }
              });
            },
            onError: (message) => {
              alert(message || 'Failed to add products to cart');
            },
          });
        } finally {
          CartUtils.setButtonLoading(button, false);
        }
      }
    }
  );
}

// Variant Options Modal Component
if (!customElements.get('quick-order-form-modal')) {
  customElements.define(
    'quick-order-form-modal',
    class QuickOrderFormModal extends HTMLElement {
      constructor() {
        super();
        this.variants = [];
        this.selectedVariant = null;
        this.init();
      }

      init() {
        this.loadVariants();
        this.setupEventListeners();
        this.updateVariant();
      }

      loadVariants() {
        const variantsScript = this.querySelector('[data-product-variants]');
        if (variantsScript) {
          try {
            this.variants = JSON.parse(variantsScript.textContent);
          } catch (e) {
            console.error('Failed to parse product variants:', e);
          }
        }
      }

      setupEventListeners() {
        // Close button
        const closeBtn = this.querySelector('[data-modal-close]');
        closeBtn?.addEventListener('click', () => this.close());

        // Overlay click
        const overlay = this.querySelector('[data-modal-overlay]');
        overlay?.addEventListener('click', () => this.close());

        // Option selects
        const optionSelects = this.querySelectorAll('[data-option-select]');
        optionSelects.forEach((select) => {
          select.addEventListener('change', () => this.updateVariant());
        });

        // Form submit
        const form = this.querySelector('form');
        form?.addEventListener('submit', (e) => {
          e.preventDefault();
          this.handleSubmit();
        });

        // Escape key to close
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !this.hasAttribute('hidden')) {
            this.close();
          }
        });
      }

      updateVariant() {
        const form = this.querySelector('form');
        if (!form) return;

        const optionSelects = this.querySelectorAll('[data-option-select]');
        const selectedOptions = {};

        optionSelects.forEach((select) => {
          const position = parseInt(select.dataset.optionPosition, 10);
          if (position) {
            selectedOptions[`option${position}`] = select.value;
          }
        });

        // Find matching variant
        this.selectedVariant = this.variants.find((variant) => {
          return (
            variant.option1 === (selectedOptions.option1 || '') &&
            variant.option2 === (selectedOptions.option2 || '') &&
            variant.option3 === (selectedOptions.option3 || '')
          );
        });

        if (this.selectedVariant) {
          const variantInput = form.querySelector('[data-variant-id]');
          if (variantInput) {
            variantInput.value = this.selectedVariant.id;
          }

          this.updatePrice(this.selectedVariant);
          this.updateStockInfo(this.selectedVariant);
          this.updateQuantityLimit(this.selectedVariant);
        }
      }

      updatePrice(variant) {
        const priceElement = this.querySelector('[data-modal-price]');
        if (priceElement && variant) {
          priceElement.textContent = CartUtils.formatPrice(variant.price);
        }
      }

      updateStockInfo(variant) {
        const stockInfo = this.querySelector('[data-modal-stock]');
        const stockText = this.querySelector('.quick-order-form-modal__stock-text');
        if (!stockInfo || !stockText || !variant) return;

        if (!variant.available) {
          stockInfo.removeAttribute('hidden');
          stockText.textContent = 'Out of Stock';
          stockText.style.color = '#999';
        } else if (variant.inventory_management === 'shopify' && variant.inventory_quantity !== null) {
          stockInfo.removeAttribute('hidden');
          stockText.textContent = `Stock: ${variant.inventory_quantity}`;
          stockText.style.color = '#000';
        } else {
          stockInfo.setAttribute('hidden', '');
        }

        const submitBtn = this.querySelector('[data-modal-submit]');
        if (submitBtn) {
          submitBtn.disabled = !variant.available;
        }
      }

      updateQuantityLimit(variant) {
        const quantityInput = this.querySelector('[data-modal-quantity]');
        if (quantityInput && variant.inventory_management === 'shopify') {
          const maxQty = variant.inventory_quantity || 0;
          quantityInput.max = maxQty > 0 ? maxQty : '';
        }
      }

      async handleSubmit() {
        if (!this.selectedVariant?.available) return;

        const submitBtn = this.querySelector('[data-modal-submit]');
        const quantityInput = this.querySelector('[data-modal-quantity]');
        const quantity = parseInt(quantityInput?.value || 1, 10);

        if (quantity < 1) return;

        CartUtils.setButtonLoading(submitBtn, true);
        this.clearModalError();

        try {
          const form = this.querySelector('form');
              await CartUtils.addToCart(
            new FormData(form),
            {
              onSuccess: () => {
                console.log('Added to cart');
                showAddedToCartModal();
                CartUtils.refreshCartCount();
                this.close();
                if (quantityInput) {
                  quantityInput.value = 1;
                }
              },
              onError: (message) => {
                this.showModalError(message);
              }
            }
          );
        } finally {
          CartUtils.setButtonLoading(submitBtn, false);
        }
      }

      showModalError(message) {
        const form = this.querySelector('form');
        if (!form) return;

        // Remove any existing error message
        this.clearModalError();

        // Create error message element
        const errorElement = document.createElement('div');
        errorElement.className = 'quick-order-form-modal__error';
        errorElement.setAttribute('role', 'alert');
        errorElement.textContent = message;

        // Insert error message before the submit button
        const submitBtn = this.querySelector('[data-modal-submit]');
        if (submitBtn) {
          submitBtn.insertAdjacentElement('beforebegin', errorElement);
        } else {
          form.appendChild(errorElement);
        }
      }

      clearModalError() {
        const existingError = this.querySelector('.quick-order-form-modal__error');
        if (existingError) {
          existingError.remove();
        }
      }

      open() {
        this.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
        
        // Focus management
        const firstInput = this.querySelector('select, input');
        if (firstInput) {
          setTimeout(() => firstInput.focus(), 100);
        }
      }

      close() {
        this.setAttribute('hidden', '');
        document.body.style.overflow = '';
      }
    }
  );
}

// Initialize modals and handle "Show Options" buttons
document.addEventListener('DOMContentLoaded', () => {
  document.addEventListener('click', (e) => {
    const optionsBtn = e.target.closest('.quick-order-form-row__options-btn');
    if (optionsBtn) {
      e.preventDefault();
      const productId = optionsBtn.dataset.productId;
      const modal = document.getElementById(`quick-order-modal-${productId}`);
      modal?.open();
    }
  });
});
